import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  QrCode, CaretLeft, FilePdf, FileImage, FileText, Cube, Package, Armchair,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { dmsApi, STATUS_STYLE, fmtDate, fmtSize } from './dmsApi';

const REGION = 'dms-qr-region';

/** 資産ラベルのURL（QR）→ 連携ターゲット {ttype, tid}。Code128 の識別子は
 *  URLではないのでここでは解決できない（バックエンドの resolve-label-code が担当）。 */
export const parseAssetQr = (decoded) => {
  let path = '', search = '';
  try {
    const u = new URL(decoded);
    path = u.pathname; search = u.search;
  } catch { return null; }
  const open = new URLSearchParams(search).get('open');
  if (!open) return null;
  if (path.startsWith('/it/assets')) return { ttype: 'it_asset', tid: open };
  if (path.startsWith('/bihin/items')) return { ttype: 'bihin_item', tid: open };
  if (path.startsWith('/engineering/equipment')) return null;   // 設備は対象外
  if (path.startsWith('/engineering')) return { ttype: 'eng_asset', tid: open };
  return null;
};

export function BarcodeScanDialog({ open, onClose, onResolved }) {
  const scannerRef = useRef(null);
  const [manual, setManual] = useState('');
  const [starting, setStarting] = useState(false);

  const stopScan = useCallback(async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); await scannerRef.current.clear(); } catch { /* noop */ }
      scannerRef.current = null;
    }
  }, []);

  const handle = useCallback(async (decoded) => {
    const raw = String(decoded || '').trim();
    // ① URL形式（QR ディープリンク）— 既に貼られたQRシール向け
    const target = parseAssetQr(raw);
    if (target) { await stopScan(); onResolved(target); return; }
    // ② 識別子そのもの（Code128 / ハンディスキャナ）— 品番・管理番号から引く
    try {
      const r = await dmsApi.resolveLabelCode(raw);
      await stopScan();
      onResolved({ ttype: r.ttype, tid: r.tid });
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || '資産ラベルではありません（技術資産・IT資産・備品のラベルを読み取ってください）');
    }
  }, [onResolved, stopScan]);

  useEffect(() => {
    if (!open) { stopScan(); return undefined; }
    let cancelled = false;
    (async () => {
      setStarting(true);
      try {
        const mod = await import('html5-qrcode');
        if (cancelled) return;
        const inst = new mod.Html5Qrcode(REGION);
        scannerRef.current = inst;
        await inst.start({ facingMode: 'environment' }, { fps: 10, qrbox: 200 },
          (decoded) => handle(decoded), () => { /* per-frame errors ignored */ });
      } catch {
        toast.error('カメラを起動できませんでした — URLを手入力してください');
      } finally { if (!cancelled) setStarting(false); }
    })();
    return () => { cancelled = true; stopScan(); };
  }, [open, handle, stopScan]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { stopScan(); onClose(); } }}>
      <DialogContent className="max-w-sm" aria-describedby={undefined} data-testid="dms-barcode-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <QrCode size={18} className="text-teal-600" /> 資産ラベルを読み取る
          </DialogTitle>
        </DialogHeader>
        <p className="text-[11px] text-zinc-500">資産ラベルのバーコード／QRをかざすと、その資産の関連書類を表示します</p>
        <div id={REGION} className="rounded-lg overflow-hidden bg-zinc-900 min-h-[220px]" data-testid="dms-qr-region" />
        {starting && <p className="text-[11px] text-zinc-400 text-center">カメラ起動中…</p>}
        <div className="flex items-center gap-1.5">
          <Input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="または品番・管理番号／ラベルURLを入力"
            className="h-9 text-xs flex-1" data-testid="dms-barcode-manual"
            onKeyDown={(e) => e.key === 'Enter' && manual.trim() && handle(manual.trim())} />
          <Button size="sm" className="h-9 bg-teal-700 hover:bg-teal-800" data-testid="dms-barcode-manual-go"
            onClick={() => manual.trim() && handle(manual.trim())}>開く</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const TTYPE_ICON = { eng_asset: Cube, it_asset: Package, bihin_item: Armchair };

const docIcon = (mime, size = 20) => {
  if (mime === 'application/pdf') return <FilePdf size={size} weight="duotone" className="text-rose-500 shrink-0" />;
  if ((mime || '').startsWith('image/')) return <FileImage size={size} weight="duotone" className="text-sky-500 shrink-0" />;
  return <FileText size={size} weight="duotone" className="text-zinc-400 shrink-0" />;
};

export function AssetDocsView({ target, onBack, onOpenDoc }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!target) return;
    setLoading(true);
    dmsApi.linked(target.ttype, target.tid)
      .then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [target]);

  if (!target) return null;
  const IconC = TTYPE_ICON[target.ttype] || Cube;

  return (
    <div className="space-y-3" data-testid="asset-docs-view">
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={onBack} aria-label="前の画面に戻る" title="戻る"
          className="inline-flex items-center justify-center h-9 w-9 rounded-md text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:outline-none" data-testid="asset-docs-back">
          <CaretLeft size={18} />
        </button>
        <IconC size={20} weight="duotone" className="text-teal-600" />
        <h2 className="text-sm font-bold text-zinc-800" data-testid="asset-docs-title">
          {data?.target_type_label || '資産'}{data?.target_label ? `: ${data.target_label}` : ''} の関連書類
        </h2>
        {data?.documents && <span className="text-[10px] text-zinc-400">{data.documents.length}件</span>}
      </div>
      {loading ? (
        <p className="py-12 text-center text-sm text-zinc-400">読み込み中...</p>
      ) : !data?.enabled ? (
        <p className="py-12 text-center text-sm text-zinc-400" data-testid="asset-docs-disabled">資産連携が無効か、アクセス権限がありません</p>
      ) : data.target_label == null ? (
        <p className="py-12 text-center text-sm text-zinc-400" data-testid="asset-docs-notfound">資産が見つかりませんでした</p>
      ) : data.documents.length === 0 ? (
        <p className="py-12 text-center text-sm text-zinc-400" data-testid="asset-docs-empty">この資産に連携された書類はありません — 資産の詳細画面から添付できます</p>
      ) : (
        <div className="space-y-1.5">
          {data.documents.map((d) => (
            <div key={d.link_id} className="flex items-center gap-2.5 rounded-lg border border-zinc-200 bg-white px-3 py-2" data-testid={`asset-doc-${d.id}`}>
              {docIcon(d.mime_type)}
              <button type="button" onClick={() => onOpenDoc(d.id)} className="min-w-0 flex-1 text-left group">
                <span className="block text-[13px] font-medium text-zinc-800 truncate group-hover:text-teal-700 group-hover:underline">{d.title}</span>
                <span className="block text-[10px] text-zinc-400 font-mono">{d.document_number} · {d.doc_type_label} · {fmtSize(d.file_size)} · {fmtDate(d.updated_at)}</span>
              </button>
              <span className={`px-1.5 py-0.5 rounded border text-[9px] font-bold shrink-0 ${STATUS_STYLE[d.status] || ''}`}>{d.status_label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
